import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';

import { getRelatedGuides } from '@/constants/relatedGuides';

const thumbnail = (url: string) =>
  `https://easy-peasy.ai/cdn-cgi/image/width=640,quality=80,format=auto/${url}`;

/**
 * "Guides and tutorials" strip shown above the footer on tool pages that have
 * matching blog posts (map in src/constants/relatedGuides.ts). The posts are
 * English-only, so other locales get nothing.
 */
export default function RelatedGuides({ dark }: { dark?: boolean }) {
  const router = useRouter();
  if (router.locale && router.locale !== router.defaultLocale) return null;

  const guides = getRelatedGuides(router.asPath);
  if (!guides.length) return null;

  return (
    <section
      aria-labelledby='related-guides-heading'
      className={dark ? 'dark bg-slate-900' : 'bg-white dark:bg-slate-900'}
    >
      <div className='mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8 lg:pt-16'>
        <div className='flex items-end justify-between gap-4'>
          <h2
            id='related-guides-heading'
            className='text-2xl font-bold tracking-tight text-gray-900 dark:text-white'
          >
            Guides and tutorials
          </h2>
          <Link
            href='/blog'
            className='shrink-0 text-sm font-semibold text-primary-600 hover:text-primary-500 dark:text-primary-400'
          >
            All guides &rarr;
          </Link>
        </div>
        <ul className='mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
          {guides.map((guide) => (
            <li key={guide.slug}>
              <Link
                href={`/blog/${guide.slug}`}
                className='group block h-full overflow-hidden rounded-xl bg-white ring-1 ring-gray-200 transition hover:shadow-md dark:bg-slate-800 dark:ring-white/10'
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={thumbnail(guide.image)}
                  alt=''
                  width={640}
                  height={360}
                  loading='lazy'
                  decoding='async'
                  className='aspect-video w-full object-cover'
                />
                <p className='p-4 text-sm font-semibold leading-snug text-gray-900 group-hover:text-primary-600 dark:text-slate-100 dark:group-hover:text-primary-400'>
                  {guide.title}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
